import { useEffect, useRef, useState } from "react";

import { ACEPTA, esArchivoExcel } from "../shared/importarExcel";

import "./css/importarExcel.css";

/* Las dos maneras de traer un Excel a FiboSheets: el botón (con su selector
 * de archivos escondido) y ARRASTRAR el archivo a la pantalla.
 *
 * ── ARRASTRAR Y SOLTAR ─────────────────────────────────────────────────────
 *
 * Quien tiene el Excel en el escritorio lo arrastra: es lo que hace con un
 * adjunto en el mail. `SoltarExcel` escucha el arrastre en TODA la ventana
 * (no hace falta apuntarle a un recuadro chiquito) y, mientras viene un
 * archivo, tapa la pantalla con «Soltá el Excel acá». Se dibuja suelto, sin
 * envolver nada: la pantalla que lo usa no tiene que cambiar su estructura.
 *
 * Al soltar se frena lo que haría el navegador (abrir el archivo y salir del
 * hub). Si lo que se soltó no es un Excel, se dice ahí mismo y no se llama a
 * nadie. Sólo se cuenta un arrastre si trae ARCHIVOS: arrastrar un texto o una
 * celda adentro de la planilla no muestra nada.
 */

const traeArchivos = (e) => {
  const tipos = e.dataTransfer && e.dataTransfer.types;
  return !!tipos && Array.from(tipos).includes("Files");
};

export function SoltarExcel({ onArchivo, activo = true, detalle = null }) {
  const [encima, setEncima] = useState(false);
  const [error, setError] = useState(null);
  const cuenta = useRef(0); // dragenter/dragleave llegan por cada elemento que se cruza
  const alSoltar = useRef(onArchivo);
  alSoltar.current = onArchivo;

  useEffect(() => {
    if (!activo) return undefined;
    const entra = (e) => {
      if (!traeArchivos(e)) return;
      e.preventDefault();
      cuenta.current += 1;
      setEncima(true);
      setError(null);
    };
    const pasa = (e) => {
      if (!traeArchivos(e)) return;
      e.preventDefault(); // sin esto, el navegador no deja soltar
      if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
    };
    const sale = (e) => {
      if (!traeArchivos(e)) return;
      cuenta.current = Math.max(0, cuenta.current - 1);
      if (!cuenta.current) setEncima(false);
    };
    const suelta = (e) => {
      if (!traeArchivos(e) && !(e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length)) return;
      e.preventDefault();
      cuenta.current = 0;
      setEncima(false);
      const f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (!f) return;
      if (!esArchivoExcel(f.name)) {
        setError(`«${f.name}» no es un Excel. Se pueden traer archivos .xlsx, .xlsb, .xls o .csv.`);
        return;
      }
      alSoltar.current(f);
    };
    window.addEventListener("dragenter", entra);
    window.addEventListener("dragover", pasa);
    window.addEventListener("dragleave", sale);
    window.addEventListener("drop", suelta);
    return () => {
      window.removeEventListener("dragenter", entra);
      window.removeEventListener("dragover", pasa);
      window.removeEventListener("dragleave", sale);
      window.removeEventListener("drop", suelta);
      cuenta.current = 0;
    };
  }, [activo]);

  if (!activo) return null;
  return (
    <>
      {encima && (
        <div className="imx-soltar" role="presentation">
          <div className="imx-recuadro">
            <b>Soltá el Excel acá</b>
            {detalle && <span>{detalle}</span>}
          </div>
        </div>
      )}
      {error && (
        <p className="panel-aviso suave imx-error" role="alert">
          <span>{error}</span>
          <button className="esp-btn chico" onClick={() => setError(null)} aria-label="Cerrar el mensaje">×</button>
        </p>
      )}
    </>
  );
}

/* El botón de importar con su selector de archivos escondido. */
export function BotonImportarExcel({ onArchivo, children, titulo, etiqueta }) {
  const ref = useRef(null);
  return (
    <>
      <button className="esp-btn" title={titulo} onClick={() => ref.current && ref.current.click()}>
        {children}
      </button>
      <input
        ref={ref} type="file" accept={ACEPTA} style={{ display: "none" }} aria-label={etiqueta}
        onChange={(e) => {
          const f = e.target.files && e.target.files[0];
          e.target.value = ""; // permitir reimportar el mismo archivo
          if (f) onArchivo(f);
        }}
      />
    </>
  );
}
